<script setup lang="ts">
import { useReCaptcha } from 'vue-recaptcha-v3';
import { useModyApi } from '~/middelware/ModyApi';
import type { RequestData } from '~/store/userdata';
import { Cart } from '~/utils/Cart';

useSeoMeta({
  title: "Veranstaltungstechnik aus Flachau",
  description: "Mody Veranstaltungstechnik aus Flachau: Bühne, Ton und Licht für Firmenfeiern, Hochzeiten, Konzerte und regionale Events im Salzburger Land.",
  ogTitle: "Veranstaltungstechnik aus Flachau | Mody",
  ogDescription: "Bühne, Ton und Licht für Firmenfeiern, Hochzeiten, Konzerte und regionale Events im Salzburger Land.",
  ogImage: "https://mody.at/homepage_header_poster.webp",
  ogImageAlt: "MODY Veranstaltungstechnik mit Bühne und Lichttechnik",
  twitterImage: "https://mody.at/homepage_header_poster.webp",
})

const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Mody Veranstaltungstechnik",
  "url": "https://mody.at",
  "telephone": "+43 664 2247433",
  "image": "https://mody.at/homepage_header_poster.webp",
  "logo": "https://mody.at/rgb_negativ.png",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Flachau",
    "postalCode": "5542",
    "addressRegion": "Salzburg",
    "addressCountry": "AT",
    "streetAddress" : "Ennspark 193"
  },
  "areaServed": [
    "Ennspongau",
    "Schladming",
    "Stadt Salzburg",
    "Flachau",
    "Altenmarkt",
    "Eben",
    "Radstadt",
    "St. Johann im Pongau",
    "Bischofshofen",
    "Land Salzburg",
    "Golling",
    "Hallein",
    "Tennengau",
    "Pongau",
    "Lungau",
    "Tamsweg",
    "St. Michael im Lungau",
    "Mauterndorf",
    "Salzburger Land"
  ],
  "priceRange": "€€",
  "sameAs": [
    "https://www.facebook.com/mody.at/",
    "https://www.instagram.com/mody_veranstaltungstechnik/",
    "https://www.linkedin.com/company/mody-veranstaltungstechnik/about/"
  ]
}

useHead({
  script: [
    { type: 'application/ld+json', innerHTML: localBusinessJsonLd }
  ]
})

// Schnellanfrage
const api = useModyApi()
const toast = useToast()
const recaptchaInstance = useReCaptcha()
const quickLoading = ref(false)
const quickSuccess = ref(false)
const quickName = ref('')
const quickEmail = ref('')
const quickDate = ref(new Date())
const quickRegion = ref('')
const quickDescription = ref('')
const referenceImageLoaded = ref(false)
const highlightedReferences = [
  {
    id: 5,
    category: 'Organisation & Ablauf',
    title: 'Krampuslauf Golling',
    description: 'Der Stagetruck im Einsatz - wenn Aufbau, Bühne und Ablauf vor Ort sauber zusammenspielen müssen.',
  },
  {
    id: 1,
    category: 'Sprache & Moderation',
    title: 'Gstanzlsingen Altenmarkt',
    description: 'Fokus auf Tontechnik und Sprachverständlichkeit - wichtig für Moderation, Wortbeiträge und Programmpunkte mit Publikum.',
  },
  {
    id: 9,
    category: 'Regionales Event',
    title: 'Stadtfest Bischofshofen',
    description: 'DJ-Set am Stagetruck - ein Beispiel für regionale Veranstaltungen mit Musik, Publikum und verlässlicher Technik.',
  }
]

async function submitQuickRequest() {
  let token
  try {
    await recaptchaInstance?.recaptchaLoaded()
    token = await recaptchaInstance?.executeRecaptcha('quick_request')
  } catch (e) {
    console.log(e)
  }
  if (!token) {
    toast.add({ detail: 'Es ist leider etwas schief gelaufen!', severity: 'error', life: 5000 })
    return
  }
  quickLoading.value = true
  const requestData = ref<RequestData>({
    name: quickName.value,
    email: quickEmail.value,
    date_from: quickDate.value,
    date_to: quickDate.value,
    company_name: '',
    address: '',
    city: quickRegion.value,
    description: quickDescription.value,
    eventDays: 1,
    telephone: '',
    subscribeNewsletter: false,
  })
  const success = await api.sendRequest(requestData, new Cart())
  quickLoading.value = false
  if (success) {
    quickSuccess.value = true
  } else {
    toast.add({ detail: 'Es ist leider etwas schief gelaufen!', severity: 'error', life: 5000 })
  }
}

</script>
<template>
  <main>
  <section class="home-hero">
    <video
      src="/homepage_header_25fps.mp4"
      preload="metadata"
      autoplay
      class="home-hero__media"
      fetchpriority="high"
      poster="/homepage_header_poster.webp"
      loop
      muted
      playsinline
      aria-hidden="true"
    />
    <div class="home-hero__overlay"></div>
    <div class="home-hero__content">
      <h1>Die Technik für dein Event</h1>
      <p class="home-hero__copy">
        Für Firmenfeiern, Kulturveranstaltungen, Hochzeiten und regionale Events – direkt aus Flachau, verlässlich geplant und unkompliziert umgesetzt.
      </p>
      <div class="home-hero__actions">
        <Button as="router-link" to="/shop/request" class="home-hero__primary">Angebot anfordern</Button>
        <Button as="router-link" to="/shop/category/setangebote" severity="secondary" variant="outlined">Setangebote ansehen</Button>
      </div>
    </div>
  </section>
  <div class="bg-[#0B0B0C] py-14 sm:py-20 px-4 sm:px-8">
    <div class="max-w-6xl mx-auto">
      <div class="split-intro">
        <div class="split-intro__heading">
          <h2 class="text-4xl sm:text-[42px] font-semibold tracking-[-0.03em] text-white leading-[1.08]">
            Verlässlich geplant, unkompliziert umgesetzt.
          </h2>
        </div>
        <p class="split-intro__copy">
          Erfahrung, gepflegte Technik und direkte Abstimmung sorgen dafür, dass dein Event reibungslos läuft. Von unserem Standort in Flachau betreuen wir Veranstaltungen im Pongau und Lungau sowie von Schladming bis in die Stadt Salzburg.
        </p>
      </div>
      
      <div class="grid gap-5 sm:gap-6 lg:gap-7 sm:grid-cols-3 mt-12 sm:mt-14">
        <!-- Vorteil 1 -->
        <div class="soft-card soft-card-dark p-7 sm:p-8 flex flex-col items-start text-left" v-animateonscroll="{ enterClass: 'animate-enter fade-in-10 animate-duration-500'}">
          <i class="pi pi-clock text-4xl text-[#E39737] mb-5"></i>
          <h3 class="text-[28px] leading-[1.15] tracking-[-0.02em] font-semibold text-white mb-3">Langjährige Erfahrung</h3>
          <p class="text-stone-300 text-m leading-relaxed">
            Seit über 25 Jahren begleiten wir Firmenfeiern, Kulturveranstaltungen, Hochzeiten, Konzerte und Events jeder Größe - mit Know-how, das man spürt.
          </p>
        </div>
        
        <!-- Vorteil 2 -->
        <div class="soft-card soft-card-dark p-7 sm:p-8 flex flex-col items-start text-left" v-animateonscroll="{ enterClass: 'animate-enter fade-in-10 animate-duration-500'}">
          <i class="pi pi-cog text-4xl text-[#4C9CD4] mb-5"></i>
          <h3 class="text-[28px] leading-[1.15] tracking-[-0.02em] font-semibold text-white mb-3">Hochwertige Technik</h3>
          <p class="text-stone-300 text-m leading-relaxed">
            Wir verwenden ausschließlich professionelle, regelmäßig gewartete Geräte für klare Sprachverständlichkeit, starken Sound und reibungslose Abläufe.
          </p>
        </div>
        
        <!-- Vorteil 3 -->
        <div class="soft-card soft-card-dark p-7 sm:p-8 flex flex-col items-start text-left" v-animateonscroll="{ enterClass: 'animate-enter fade-in-10 animate-duration-500'}">
          <i class="pi pi-comments text-4xl text-[#E39737] mb-5"></i>
          <h3 class="text-[28px] leading-[1.15] tracking-[-0.02em] font-semibold text-white mb-3">Persönliche Beratung</h3>
          <p class="text-stone-300 text-m leading-relaxed">
            Wir planen sauber, kommunizieren direkt und finden auch für kleinere Feste, Setangebote oder Selbstabholungen die passende Lösung.
          </p>
        </div>
      </div>

    </div>
  </div>
  <div class="bg-[#111214] py-14 sm:py-20 px-6">
    <div class="max-w-6xl mx-auto">
      <div class="split-intro mb-8 sm:mb-10 lg:mb-12">
        <div class="split-intro__heading">
          <h2 class="text-4xl sm:text-[42px] font-semibold tracking-[-0.03em] text-white leading-[1.08]">Erfahrung aus echten Veranstaltungen</h2>
        </div>
        <p class="split-intro__copy">
          Ob organisiertes Bühnenprogramm, Moderation mit viel Sprache oder ein regionales Fest mit Publikum: Unsere Referenzen zeigen, wie unterschiedlich Veranstaltungen sein können - und dass die Technik trotzdem sauber zusammenpassen muss.
        </p>
      </div>

      <div class="reference-image-shell" :aria-busy="!referenceImageLoaded">
        <Skeleton v-if="!referenceImageLoaded" class="reference-image-skeleton" aria-hidden="true" />
        <NuxtImg
          src="/mody-eventtechnik-leinwand-lichtstimmung.webp"
          format="webp"
          quality="88"
          sizes="100vw sm:100vw md:960px lg:1152px"
          loading="lazy"
          alt="Professionelles Lichtsetup mit MODY Logo für Firmenveranstaltungen"
          class="reference-image"
          :class="{ 'reference-image--loaded': referenceImageLoaded }"
          @load="referenceImageLoaded = true"
          @error="referenceImageLoaded = true"
        />
      </div>

      <div class="grid gap-4 sm:gap-5 lg:gap-6 lg:grid-cols-3">
        <NuxtLink
          v-for="reference in highlightedReferences"
          :key="reference.id"
          :to="`/references/${reference.id}`"
          class="no-underline"
        >
          <div class="soft-card soft-card-mid h-full p-6 sm:p-7 transition-colors">
            <h3 class="text-xl font-semibold text-white mb-2">{{ reference.title }}</h3>
            <p class="reference-category">{{ reference.category }}</p>
            <p class="text-stone-300 leading-relaxed">{{ reference.description }}</p>
          </div>
        </NuxtLink>
      </div>

      <div class="proof-cta flex flex-col sm:flex-row gap-3 justify-center mt-8 sm:mt-10 text-center">
        <Button as="router-link" to="/references" class="px-8">Alle Referenzen ansehen</Button>
        <Button as="router-link" to="/shop/request" severity="secondary" class="px-8">Passendes Angebot anfragen</Button>
      </div>
    </div>
  </div>
  <LazySectionImageText
    id="leistungen"
    title="Bühne"
    img-url="/stagetruck-mauterndorf-original.jpg"
    img-alt="StageTruck von MODY beim Mittelalterfest in Mauterndorf"
  >
    Unser StageTruck ist in drei Varianten verfügbar und je nach Ausführung in rund 40 Minuten aufgebaut. Für kleinere Veranstaltungen bieten wir modulare Bühnenelemente. Gemeinsam wählen wir die passende Bühne für Veranstaltungsort, Programm und Publikum.<br><br>
    <Button as="router-link" to="/shop/category/stagetruck" class="py-1">StageTruck ansehen</Button>
  </LazySectionImageText>
  <LazySectionImageText title="Ton" img-url="/Ton.jpg">
    Beschallung und Mikrofonie stimmen wir auf Raum, Publikum und Programm ab – von klarer Sprachübertragung bis zu kraftvollem Live-Sound. Für kleinere Feiern bieten wir komplette Tonanlagen und Mikrofon-Sets zur Selbstabholung. Auf Wunsch übernehmen wir Aufbau und technische Betreuung vor Ort.<br><br>
    <Button as="router-link" to="/shop/category/tontechnik" class="py-1">Tontechnik ansehen</Button>
  </LazySectionImageText>
  <LazySectionImageText title="Licht" img-url="/Licht.png">
    Von stimmungsvollem Raumlicht bis zur inszenierten Bühnenshow entwickeln wir ein Lichtkonzept, das zur Veranstaltung passt. Dazu gehören unter anderem Scheinwerfer, Effektlicht und professionelle Lichtsteuerung. Auf Wunsch übernehmen wir Planung, Aufbau und Betreuung während des Events.<br><br>
    <Button as="router-link" to="/shop/category/lichttechnik" class="py-1">Lichttechnik ansehen</Button>
  </LazySectionImageText>

  <!-- Schnellanfrage -->
  <div class="bg-[#111214] py-14 sm:py-20 px-6">
    <div class="w-full max-w-3xl mx-auto request-shell">
      <Toast />
      <h2 class="text-4xl sm:text-[42px] font-semibold tracking-[-0.03em] text-white text-center mb-3 leading-[1.08]">Sag uns kurz, was du planst</h2>
      <p class="text-stone-300 text-center mb-10 sm:mb-12 request-copy">Ob Firmenveranstaltung, Hochzeit oder Vereinsfest - wir melden uns mit einem unverbindlichen Angebot.</p>

      <div v-if="quickSuccess" class="text-center py-10">
        <i class="pi pi-check-circle text-5xl text-[#E39737] mb-4"></i>
        <div class="text-xl font-semibold text-white mb-2">Anfrage gesendet!</div>
        <div class="text-stone-400">Wir melden uns so schnell wie möglich bei dir.</div>
      </div>

      <form v-else-if="!quickLoading" @submit.prevent="submitQuickRequest" class="request-form grid grid-cols-1 sm:grid-cols-2 gap-4">
        <p class="request-required-note sm:col-span-2"><span aria-hidden="true">*</span> Pflichtfeld</p>
        <div class="request-field">
          <label for="quick-name">Name <span aria-hidden="true">*</span></label>
          <InputText id="quick-name" v-model="quickName" name="name" autocomplete="name" required placeholder="Vor- und Nachname" class="w-full" />
        </div>
        <div class="request-field">
          <label for="quick-email">E-Mail <span aria-hidden="true">*</span></label>
          <InputText id="quick-email" v-model="quickEmail" name="email" type="email" autocomplete="email" required placeholder="name@beispiel.at" class="w-full" />
        </div>
        <div class="request-field">
          <label for="quick-date">Datum des Events <span aria-hidden="true">*</span></label>
          <DatePicker input-id="quick-date" v-model="quickDate" required date-format="dd.mm.yy" placeholder="TT.MM.JJJJ" class="w-full" />
        </div>
        <div class="request-field">
          <label for="quick-region">Ort oder Region</label>
          <InputText id="quick-region" v-model="quickRegion" name="address-level2" autocomplete="address-level2" placeholder="z. B. Flachau" class="w-full" />
        </div>
        <div class="request-field sm:col-span-2">
          <label for="quick-description">Kurze Beschreibung des Events</label>
          <Textarea id="quick-description" v-model="quickDescription" name="description" placeholder="Was planst du und wobei können wir helfen?" class="w-full" rows="3" />
        </div>
        <div class="sm:col-span-2 flex justify-center mt-2">
          <Button type="submit" class="px-10">Unverbindlich anfragen</Button>
        </div>
      </form>

      <div v-if="quickLoading" class="flex justify-center items-center py-10">
        <ProgressSpinner style="width: 50px; height: 50px;" strokeWidth="4" />
      </div>
    </div>
  </div>

  </main>
</template>
<style scoped lang="scss">


.home-hero{
  position: relative;
  min-height: calc(100svh - 100px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  isolation: isolate;
  background: #070708;

  @include tablet(){
    min-height: calc(100svh - 100px);
  }
}

.home-hero__media{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right center;
  z-index: -2;

  @include tablet(){
    object-position: center;
  }
}

.home-hero__overlay{
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(90deg, rgba(5, 5, 6, 0.9) 0%, rgba(5, 5, 6, 0.62) 60%, rgba(5, 5, 6, 0.34) 100%),
    linear-gradient(0deg, rgba(5, 5, 6, 0.98) 0%, rgba(5, 5, 6, 0.74) 48%, rgba(5, 5, 6, 0.14) 82%);

  @include tablet(){
    background:
      linear-gradient(90deg, rgba(5, 5, 6, 0.88) 0%, rgba(5, 5, 6, 0.48) 48%, rgba(5, 5, 6, 0.18) 76%),
      linear-gradient(0deg, rgba(5, 5, 6, 0.98) 0%, rgba(5, 5, 6, 0.68) 42%, rgba(5, 5, 6, 0.06) 78%);
  }
}

.home-hero__content{
  width: min(100%, 1152px);
  margin: 0 auto;
  padding: 3.5rem 1.5rem 8rem;

  h1{
    max-width: 9ch;
    color: white;
    font-size: clamp(3.2rem, 8vw, 6.6rem);
    line-height: 0.94;
    letter-spacing: -0.04em;
    font-weight: 750;
    text-wrap: balance;
  }

  @include tablet(){
    padding: 5rem 2rem 5.5rem;
  }
}

.home-hero__copy{
  max-width: 690px;
  margin-top: 1.5rem;
  color: rgb(231 229 228);
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  line-height: 1.6;
  text-wrap: balance;
}

.home-hero__actions{
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 2rem;

  a{
    text-decoration: none;
  }

  :deep(.p-button){
    width: 100%;
    justify-content: center;
    min-height: 50px;
    padding-inline: 1.5rem;
    border-radius: 999px;
    font-weight: 650;
  }

  @include phone(){
    flex-direction: row;
    align-items: center;

    :deep(.p-button){
      width: auto;
    }
  }
}

:deep(.home-hero__primary){
  background: #E39737;
  border-color: #E39737;
  color: #111214;
}

.soft-card{
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: $public-radius-large;
  transition: border-color 180ms $public-ease, background-color 180ms $public-ease;
}

.soft-card-dark{
  background: rgba(23,24,26,0.88);
}

.soft-card-mid{
  background: rgba(19,20,22,0.9);
  &:hover{
    border-color: rgba(227,151,55,0.26);
  }
}

.reference-image-shell{
  position: relative;
  width: 100%;
  max-width: 64rem;
  height: 240px;
  margin: 0 auto 2rem;
  overflow: hidden;
  border-radius: $public-radius-large;
  background: $public-surface-raised;

  @include tablet(){
    height: 320px;
    margin-bottom: 2.5rem;
  }

  @include desktop(){
    height: 420px;
  }
}

.reference-image-skeleton{
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.reference-image{
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 220ms $public-ease;
}

.reference-image--loaded{
  opacity: 1;
}

.reference-category{
  margin: 0 0 0.85rem;
  color: #87badd;
  font-size: 0.82rem;
  font-weight: 600;
}

.split-intro{
  display: grid;
  gap: 20px;
  align-items: end;
}

.split-intro__heading{
  max-width: 520px;
}

.split-intro__copy{
  max-width: 640px;
  color: rgb(214 211 209);
  font-size: 18px;
  line-height: 1.68;
}

@include desktop(){
  .split-intro{
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.1fr);
    gap: 54px;
    justify-content: space-between;
  }
}

.proof-cta{
  :deep(a.p-button){
    text-decoration: none;
  }

  :deep(a.p-button:not(.p-button-secondary)){
    color: #111214;
  }

  :deep(.p-button-secondary){
    opacity: 0.82;
  }
}

.request-shell{
  background: rgba(19,20,22,0.92);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: $public-radius-large;
  padding: 2rem 1.5rem;

  @include desktop(){
    padding: 3rem;
  }
}

.request-copy{
  max-width: 760px;
  margin-inline: auto;
}

.request-required-note{
  margin: 0 0 0.15rem;
  color: $public-text-muted;
  font-size: 0.82rem;
  text-align: right;

  span{
    color: #E39737;
  }
}

.request-field{
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label{
    color: rgb(231 229 228);
    font-size: 0.9rem;
    font-weight: 600;

    span{
      color: #E39737;
    }
  }
}

.request-form{
  :deep(.p-inputtext),
  :deep(.p-datepicker-input),
  :deep(textarea){
    background: rgba(15,16,18,0.92);
    border-color: rgba(255,255,255,0.08);
    color: rgb(245 245 244);
    border-radius: $public-radius-control;
  }

  :deep(.p-button){
    border-radius: $public-radius-control;
  }
}

a{
  color: var(--p-highlight-color);
  text-decoration: underline;
  font-weight: 500;
  &:hover{
    color: var(--p-highlight-hover-color);
  }
}
</style>
